import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { portalApi } from '@lipaflow/portal-core'
import { Button, ErrorState } from '@lipaflow/ui'

export interface Tenancy {
  lease_id: string; account_id?: string | null; unit: string; property_name: string | null; monthly_rent: string; deposit: string
  started_on: string; ends_on: string | null; notice_given_on: string | null; notice_given_by: string | null
  notice_period_months: number; notice_would_end_on: string; status: string; can_manage: boolean
  called_off_on?: string | null
  /** Rule 4.4: where my agreement to the terms is; "waiting_for_tenant" asks me. */
  terms_state?: string | null
  terms_state_label?: string | null
  /** Moved out: my itemised settlement is there to read (rule 10.9). */
  has_settlement?: boolean
}
interface Tenancies { items: Tenancy[]; total: number; pages: number }
/** A home by its building and number, "Kilimani Heights · A1" (rule 3.5). */
export const homeName = (t: Tenancy) => [t.property_name, t.unit].filter(Boolean).join(' · ')
export const longDate = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-KE', { day: 'numeric', month: 'long', year: 'numeric' })

export default function TenancyPicker({ children }: { children: (tenancy: Tenancy) => React.ReactNode }) {
  const [page, setPage] = useState(1)
  // A link from an SMS opens on its tenancy (?tenancy=…): the terms to agree, or the move-out statement.
  const [selected, setSelected] = useState<string | null>(() => new URLSearchParams(window.location.search).get('tenancy'))
  const tenancies = useQuery({
    queryKey: ['portal-tenancies', page],
    queryFn: async () => (await portalApi.get<Tenancies>('/tenancy/list', { params: { page, per_page: 20 } })).data,
    retry: false,
  })
  if (tenancies.isLoading) return <p role="status">Loading your homes…</p>
  if (tenancies.isError) return <ErrorState title="Unable to load your tenancy" onRetry={() => { void tenancies.refetch() }} />
  const items = tenancies.data?.items ?? []
  const current = items.find((item) => item.lease_id === selected) ?? items.find((item) => item.can_manage) ?? items[0]
  return <div className="space-y-4">
    {items.length > 1 && <label className="block text-sm font-medium">Choose your tenancy
      <select className="mt-2 w-full rounded-lg border border-stone-300 bg-white px-3 py-2" value={current?.lease_id ?? ''} onChange={(e) => setSelected(e.target.value)}>
        {items.map((item) => <option key={item.lease_id} value={item.lease_id}>
          {homeName(item)} · {item.called_off_on ? 'Called off' : item.status === 'terminated' ? 'Ended' : item.can_manage ? 'Current' : 'Upcoming'} · {longDate(item.started_on)}
        </option>)}
      </select>
    </label>}
    {(tenancies.data?.pages ?? 0) > 1 && <div className="flex items-center justify-between gap-3">
      <Button variant="secondary" disabled={page === 1} onClick={() => { setSelected(null); setPage(page - 1) }}>Previous homes</Button>
      <span>Page {page} of {tenancies.data?.pages}</span>
      <Button variant="secondary" disabled={page >= (tenancies.data?.pages ?? 1)} onClick={() => { setSelected(null); setPage(page + 1) }}>Next homes</Button>
    </div>}
    {current ? children(current) : <p className="rounded-xl bg-white p-6">You have no tenancies to show.</p>}
  </div>
}
